import { ListContainer, ListContext, SearchBar, Stack, pn } from "@compass/core";
import { ListFooter } from "components";
import {
  StudentViewResource,
  SurveyResponseListDto,
  SurveyResponseListFilter,
  SurveyResponseListResource,
  SurveyResponseFilter as SurveyResponseFilterModel,
  Permission,
  PermissionAction,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useInitListFilter, useList, useListFilter } from "hooks";
import { useEffect, useState } from "react";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { SurveyResponseTable } from "pages/cobro/survey-responses/SurveyResponseListPage/SurveyResponseTable";
import { SurveyResponseFilter } from "pages/cobro/survey-responses/SurveyResponseFilter";

export const StudentForms = (props: {
  initialLoad: boolean;
  studentId: number;
  activeSchoolYear: number | undefined;
  onGetList: () => void;
  viewResource: StudentViewResource;
}) => {
  const { listFilter, setListFilter } = useInitListFilter<SurveyResponseListFilter>({
    sortObjects: [{ property: pn<SurveyResponseListDto>("completedDate"), isDesc: true }],
  });
  const [initListFilter] = useState<SurveyResponseListFilter>(listFilter);
  const [listItems, setListItems] = useState<SurveyResponseListDto[]>([]);
  const [resource, setResource] = useState<SurveyResponseListResource>({} as SurveyResponseListResource);
  const [total, setTotal] = useState<number>();
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, loadList, applyPage, applyPageSize, applySortMethod, applySort, applySearch } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      if (isAuthorized(Permission.Form, PermissionAction.View)) {
        ApiService.surveyResponses.getStudentList(listFilter, props.studentId).then((response) => {
          setListItems(response);
        });
      }
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      if (isAuthorized(Permission.Form, PermissionAction.View)) {
        ApiService.surveyResponses.getListTotal(listFilter).then((response) => {
          setTotal(response);
        });
      }
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.surveyResponses.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<SurveyResponseListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <SurveyResponseFilter
        filter={listFilter.surveyResponseFilter || ({} as SurveyResponseFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.surveyResponseFilter = filter;
            })
          )
        }
      />
    ),
  });

  return (
    <Stack spacing="md" height="100%" overflow="auto">
      <ListContainer
        fullHeight
        header={
          <Stack direction="row" justifyContent="space-between">
            <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
            {listFilterNode}
          </Stack>
        }
        tableContainer={
          <ListContext.Provider value={{ loadList: () => loadList(listFilter) }}>
            <SurveyResponseTable
              tableContainerRef={tableContainerRef}
              surveyResponses={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
            />
          </ListContext.Provider>
        }
        footer={
          <ListFooter
            total={total}
            size={listFilter.pageSize}
            page={listFilter.page}
            onPageChange={applyPage}
            onSizeChange={applyPageSize}
            onSortMethodChange={applySortMethod}
          />
        }
      />
    </Stack>
  );
};
